import { useEffect, useRef, useState } from "react";
import { Camera, CameraOff, Loader2, ShieldCheck } from "lucide-react";

const MODEL_URL = "/models";
const SAMPLE_INTERVAL_MS = 2500;
const MIN_CONFIDENCE = 0.4;

let modelsPromise = null;
function loadModels() {
  if (!modelsPromise) {
    modelsPromise = import("face-api.js").then(async (faceapi) => {
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceExpressionNet.loadFromUri(MODEL_URL),
      ]);
      return faceapi;
    });
  }
  return modelsPromise;
}

/**
 * Optional, client-side-only facial expression sampler shown alongside the assessment.
 * No video or images ever leave the browser — only the detected expression label +
 * confidence per sample is reported via onSample, mirroring the mouse/timing telemetry.
 */
export default function ExpressionCamera({ onSample, active = true }) {
  const [status, setStatus] = useState("idle"); // idle | loading | running | denied | unsupported | error
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const faceapiRef = useRef(null);
  const onSampleRef = useRef(onSample);
  onSampleRef.current = onSample;

  const stop = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => () => stop(), []);

  const enable = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus("unsupported");
      return;
    }
    setStatus("loading");
    try {
      const [faceapi, stream] = await Promise.all([
        loadModels(),
        navigator.mediaDevices.getUserMedia({ video: { width: 320, height: 240 }, audio: false }),
      ]);
      faceapiRef.current = faceapi;
      streamRef.current = stream;
      // Flip to "running" first so the <video> element actually mounts — the stream gets
      // attached in the effect below, once the ref exists.
      setStatus("running");
    } catch (e) {
      setStatus(e?.name === "NotAllowedError" ? "denied" : "error");
      stop();
    }
  };

  // The <video> element only exists in the DOM once status === "running", so the stream
  // can't be attached inside enable() itself (the ref is still null at that point).
  useEffect(() => {
    if (status !== "running" || !videoRef.current || !streamRef.current || !faceapiRef.current) return undefined;
    const faceapi = faceapiRef.current;
    const video = videoRef.current;
    video.srcObject = streamRef.current;
    video.play().catch(() => {});

    const options = new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 });
    intervalRef.current = setInterval(async () => {
      if (video.readyState < 2) return;
      try {
        const result = await faceapi.detectSingleFace(video, options).withFaceExpressions();
        if (!result?.expressions) return;
        const [expression, confidence] = Object.entries(result.expressions).sort((a, b) => b[1] - a[1])[0];
        if (confidence >= MIN_CONFIDENCE) onSampleRef.current?.(expression, confidence);
      } catch {
        // transient detection failure — skip this tick
      }
    }, SAMPLE_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [status]);

  useEffect(() => {
    if (!active) stop();
  }, [active]);

  if (status === "idle") {
    return (
      <div data-testid="expression-camera-prompt" className="card-brutal p-4 mb-6 bg-[#E9D5FF] flex flex-wrap items-center gap-3">
        <Camera size={20} strokeWidth={2.5} className="shrink-0" />
        <div className="flex-1 min-w-[220px]">
          <div className="label-mono">Optional: expression insights</div>
          <p className="text-sm text-[#0A0A0A]/80 mt-0.5">
            Open your camera so we can add a light-touch expression read to your report. Nothing is recorded or
            uploaded — only a mood label per moment stays on your device until you submit.
          </p>
        </div>
        <button
          data-testid="enable-camera-btn"
          onClick={enable}
          className="btn-brutal bg-white px-4 py-2 text-sm flex items-center gap-2 shrink-0"
        >
          <Camera size={14} strokeWidth={2.5} /> Turn on camera
        </button>
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div className="card-brutal p-4 mb-6 bg-[#E9D5FF] flex items-center gap-3 text-sm">
        <Loader2 size={16} className="animate-spin" /> Getting your camera ready…
      </div>
    );
  }

  if (status === "denied" || status === "unsupported" || status === "error") {
    return (
      <div data-testid="expression-camera-unavailable" className="card-brutal p-4 mb-6 bg-[#FFDDBF] flex items-center gap-3 text-sm">
        <CameraOff size={18} strokeWidth={2.5} className="shrink-0" />
        <span>
          {status === "denied"
            ? "Camera access wasn't granted — no problem, you can still take the assessment as usual."
            : "Camera-based expression insights aren't available on this device/browser — continuing without it."}
        </span>
      </div>
    );
  }

  return (
    <div data-testid="expression-camera-running" className="card-brutal p-3 mb-6 bg-[#A7F3D0] flex items-center gap-3">
      <video ref={videoRef} muted autoPlay playsInline className="w-20 h-16 object-cover rounded-lg border-2 border-[#0A0A0A]" />
      <div className="text-sm flex items-center gap-1.5">
        <ShieldCheck size={14} strokeWidth={2.5} />
        Reading expressions on-device — nothing is uploaded.
      </div>
    </div>
  );
}
